import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { useBrand } from "@/lib/brand";
import { PageHeader, StatusBadge, PageLoader, Btn, TextInput, TextArea, brandName } from "@/lib/ui";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus } from "lucide-react";

const COLUMNS = ["TO DO", "IN PROGRESS", "WAITING", "REVIEW", "DONE", "CANCELLED"];
const TONE = { "TO DO": "muted", "IN PROGRESS": "info", "WAITING": "warn", "REVIEW": "gold", "DONE": "ok", "CANCELLED": "err" };

function NewTask({ brand }) {
  const api = useApi();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", priority: "normal", assigned_agent: "" });
  const create = useMutation({
    mutationFn: () => api.post("/tasks", { ...form, brand_id: brand }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tasks"] }); setOpen(false); setForm({ title: "", description: "", priority: "normal", assigned_agent: "" }); toast.success("Task created"); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Btn data-testid="new-task-btn"><Plus className="h-4 w-4" /> New Task</Btn></DialogTrigger>
      <DialogContent className="bg-popover border-border">
        <DialogHeader><DialogTitle className="font-head">New Task</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <TextInput label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title" />
          <TextArea rows={2} label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <TextInput label="Priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} />
          <TextInput label="Assigned AI Agent (code, optional)" placeholder="GGG-06" value={form.assigned_agent} onChange={(e) => setForm({ ...form, assigned_agent: e.target.value })} />
        </div>
        <DialogFooter><Btn onClick={() => create.mutate()} disabled={!form.title || create.isPending} data-testid="task-submit">Create</Btn></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Tasks() {
  const api = useApi();
  const qc = useQueryClient();
  const { brand } = useBrand();
  const { data: tasks = [], isLoading } = useQuery({ queryKey: ["tasks", brand], queryFn: () => api.get(`/tasks?brand_id=${brand}`) });
  const move = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/tasks/${id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
    onError: (e) => toast.error(e.message),
  });

  if (isLoading) return <PageLoader />;

  return (
    <div>
      <PageHeader kicker={`${brandName(brand)} · Operations`} title="Tasks"><NewTask brand={brand} /></PageHeader>
      <div className="flex gap-3 overflow-x-auto pb-4">
        {COLUMNS.map((col) => {
          const items = tasks.filter((t) => t.status === col);
          return (
            <div key={col} data-testid={`task-col-${col.replace(/\s+/g, "-")}`} className="w-64 shrink-0">
              <div className="flex items-center justify-between mb-2 px-1">
                <StatusBadge tone={TONE[col]}>{col}</StatusBadge>
                <span className="text-xs font-mono text-muted-foreground">{items.length}</span>
              </div>
              <div className="space-y-2 min-h-[100px] bg-surface/30 border border-border rounded-md p-2">
                {items.length === 0 ? <div className="text-[11px] text-muted-foreground/60 text-center py-6">Empty</div> :
                  items.map((t) => (
                    <div key={t.id} data-testid={`task-card-${t.id}`} className="bg-surface border border-border rounded-md p-3">
                      <div className="text-sm font-medium leading-tight">{t.title}</div>
                      {t.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</div>}
                      <div className="flex items-center gap-2 mt-2 text-[10px] font-mono text-muted-foreground">
                        <span>{t.priority}</span>{t.assigned_agent && <span>· {t.assigned_agent}</span>}
                      </div>
                      <select
                        data-testid={`task-status-${t.id}`}
                        value={t.status}
                        onChange={(e) => move.mutate({ id: t.id, status: e.target.value })}
                        className="mt-2 w-full h-7 rounded border border-border bg-surface-elevated text-[11px] px-1.5"
                      >
                        {COLUMNS.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
